import React from 'react';
import { FaPlus, FaTrash, FaInfoCircle } from 'react-icons/fa';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { textarea } from '../../utils/designTokens';

/**
 * AgentFormStep5 - Example Interactions
 */
const AgentFormStep5 = ({ formData, onChange }) => {
  const addExample = () => {
    const newExamples = [
      ...(formData.example_interactions || []),
      { user: '', assistant: '' },
    ];
    onChange({ target: { name: 'example_interactions', value: newExamples } });
  };

  const removeExample = (index) => {
    const newExamples = (formData.example_interactions || []).filter((_, i) => i !== index);
    onChange({ target: { name: 'example_interactions', value: newExamples } });
  };

  const updateExample = (index, field, value) => {
    const newExamples = [...(formData.example_interactions || [])];
    newExamples[index] = { ...newExamples[index], [field]: value };
    onChange({ target: { name: 'example_interactions', value: newExamples } });
  };

  const loadExampleSet = () => {
    const examples = [
      {
        user: 'Can you help me analyze this dataset?',
        assistant: 'Of course! I\'d be happy to help you analyze your dataset. Please share the dataset with me, and let me know what specific insights or patterns you\'re looking for.',
      },
      {
        user: 'What are the key trends in the data?',
        assistant: 'Based on my analysis, I\'ve identified three key trends: 1) Sales increased by 25% in Q3, 2) Customer retention improved by 15%, and 3) Mobile traffic doubled compared to last quarter.',
      },
    ];
    onChange({ target: { name: 'example_interactions', value: examples } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-spooky-ghost-white mb-2">
          Example Interactions
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Provide sample conversations to demonstrate how your agent should behave. This helps users understand what to expect.
        </p>
      </div>

      {/* Info Card */}
      <Card padding="comfortable" className="bg-blue-50 dark:bg-blue-900/10 border-2 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white mb-2">
              Why Add Examples?
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Example interactions serve multiple purposes:
            </p>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 mt-2">
              <li>• Show users how to interact with the agent</li>
              <li>• Demonstrate the agent's capabilities and limitations</li>
              <li>• Set expectations for response quality and format</li>
              <li>• Help potential users evaluate if this agent fits their needs</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Example Interactions List */}
      {formData.example_interactions && formData.example_interactions.length > 0 && (
        <div className="space-y-4">
          {formData.example_interactions.map((example, index) => (
            <Card key={index} padding="comfortable" className="bg-gray-50 dark:bg-spooky-bg-light">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white">
                  Example {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => removeExample(index)}
                  className="text-red-500 hover:text-red-600 transition"
                  title="Remove example"
                >
                  <FaTrash />
                </button>
              </div>

              {/* User Message */}
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  User Message
                </label>
                <textarea
                  value={example.user}
                  onChange={(e) => updateExample(index, 'user', e.target.value)}
                  placeholder="What would a user ask?"
                  rows={2}
                  className={textarea}
                />
              </div>

              {/* Assistant Response */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Agent Response
                </label>
                <textarea
                  value={example.assistant}
                  onChange={(e) => updateExample(index, 'assistant', e.target.value)}
                  placeholder="How should the agent respond?"
                  rows={3}
                  className={textarea}
                />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={addExample}
          className="border-pink-500 text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20"
        >
          <FaPlus className="inline mr-2" />
          Add Example
        </Button>

        {(!formData.example_interactions || formData.example_interactions.length === 0) && (
          <Button
            type="button"
            variant="ghost"
            onClick={loadExampleSet}
          >
            Load Sample Examples
          </Button>
        )}
      </div>

      {/* Empty State */}
      {(!formData.example_interactions || formData.example_interactions.length === 0) && (
        <Card padding="comfortable" className="bg-gray-50 dark:bg-spooky-bg-light">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            No example interactions added yet. Examples are optional but highly recommended to help users understand your agent.
          </p>
        </Card>
      )}

      {/* Note */}
      <Card padding="default" className="bg-yellow-50 dark:bg-yellow-900/10 border-2 border-yellow-200 dark:border-yellow-800">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong>Optional:</strong> While examples are not required, they significantly improve user experience and help showcase your agent's capabilities.
        </p>
      </Card>
    </div>
  );
};

export default AgentFormStep5;
